import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useOrgPath } from '@/lib/pathFor'

export interface PortSearchResult {
  code: string
  name: string
  country_code: string
  country_name: string
  code_source: string
  code_source_label: string
  canonical_code: string | null
  label: string
  alternate_codes?: string[]
}

// A stored value matches a result if it equals the canonical code or any of its
// alternate (carrier/common) codes — so legacy values still hydrate.
function matchesValue(port: PortSearchResult, value: string): boolean {
  return port.code === value || (port.alternate_codes ?? []).includes(value)
}

interface PortComboboxProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  invalidMessage?: string
  inputId?: string
  initialPort?: PortSearchResult | null
  allowUnresolvedInitialValue?: boolean
  onValidityChange?: (valid: boolean) => void
}

export default function PortCombobox({
  value,
  onChange,
  placeholder = 'Search code or port name...',
  disabled = false,
  invalidMessage,
  inputId,
  initialPort,
  allowUnresolvedInitialValue = false,
  onValidityChange,
}: PortComboboxProps) {
  const pathFor = useOrgPath()
  const containerRef = useRef<HTMLDivElement>(null)
  const initialValueRef = useRef(value)
  const userEditedRef = useRef(false)
  const initialSelected = initialPort && matchesValue(initialPort, value) ? initialPort : null
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(initialSelected?.code || value || '')
  const [results, setResults] = useState<PortSearchResult[]>(initialSelected ? [initialSelected] : [])
  const [selected, setSelected] = useState<PortSearchResult | null>(initialSelected)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [hydrationFailed, setHydrationFailed] = useState(false)
  const onValidityChangeRef = useRef(onValidityChange)

  useEffect(() => {
    onValidityChangeRef.current = onValidityChange
  }, [onValidityChange])

  useEffect(() => {
    if (!value) {
      const timeout = window.setTimeout(() => {
        setSelected(null)
        setQuery('')
        setResults([])
        setHydrationFailed(false)
        onValidityChangeRef.current?.(true)
      }, 0)
      return () => window.clearTimeout(timeout)
    }

    if (selected && matchesValue(selected, value)) return

    if (open) return

    let cancelled = false
    fetch(pathFor(`/ports/search?q=${encodeURIComponent(value)}`), { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('hydrate failed')))
      .then((data: PortSearchResult[]) => {
        if (cancelled) return
        const exact = data.find((port) => matchesValue(port, value))
        setSelected(exact || null)
        setQuery(exact ? exact.code : value)
        setResults(exact ? [exact] : [])
        const allowCurrentUnresolvedValue = allowUnresolvedInitialValue &&
          !userEditedRef.current &&
          value === initialValueRef.current
        setHydrationFailed(!exact && !allowCurrentUnresolvedValue)
        onValidityChangeRef.current?.(Boolean(exact) || allowCurrentUnresolvedValue)
      })
      .catch(() => {
        if (cancelled) return
        const allowCurrentUnresolvedValue = allowUnresolvedInitialValue &&
          !userEditedRef.current &&
          value === initialValueRef.current
        setSelected(null)
        setQuery(value)
        setHydrationFailed(!allowCurrentUnresolvedValue)
        onValidityChangeRef.current?.(allowCurrentUnresolvedValue)
      })

    return () => {
      cancelled = true
    }
  }, [allowUnresolvedInitialValue, open, pathFor, selected, value])

  useEffect(() => {
    if (!open || query.trim().length === 0) {
      return
    }
    if (selected?.code === query) return

    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      setLoading(true)
      setError('')
      fetch(pathFor(`/ports/search?q=${encodeURIComponent(query)}`), {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('search failed')))
        .then((data: PortSearchResult[]) => {
          setResults(data)
          setHighlightedIndex(0)
        })
        .catch((searchError) => {
          if (searchError.name !== 'AbortError') {
            setError('Could not search ports')
            setResults([])
          }
        })
        .finally(() => setLoading(false))
    }, 150)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [open, pathFor, query, selected?.code])

  useEffect(() => {
    function handleDocumentClick(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [])

  function selectPort(port: PortSearchResult) {
    userEditedRef.current = true
    setSelected(port)
    setQuery(port.code)
    setResults([port])
    setHydrationFailed(false)
    onChange(port.code)
    onValidityChange?.(true)
    setOpen(false)
  }

  function handleInputChange(nextQuery: string) {
    userEditedRef.current = true
    setQuery(nextQuery)
    setSelected(null)
    setHydrationFailed(false)
    onChange(nextQuery)
    onValidityChange?.(nextQuery.trim().length === 0)
    setOpen(true)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setHighlightedIndex((index) => Math.min(index + 1, results.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter' && open && results[highlightedIndex]) {
      event.preventDefault()
      selectPort(results[highlightedIndex])
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const invalid = Boolean(invalidMessage) || hydrationFailed

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          id={inputId}
          value={selected ? selected.code : query}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(event) => handleInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          className={`h-9 pr-14 text-[13px] ${invalid ? 'border-red-300 bg-red-50 focus-visible:ring-red-200' : ''}`}
          aria-invalid={invalid}
          title={invalid ? 'Select a port from the dropdown before saving.' : undefined}
        />
        {loading && (
          <Loader2 className="pointer-events-none absolute right-8 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          className="absolute right-1 top-1.5 h-6 w-6 cursor-pointer"
          aria-label="Toggle port search"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>

        {open && !disabled && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-white p-1 shadow-lg">
            {error && <div className="px-2 py-2 text-xs text-red-600">{error}</div>}
            {!loading && !error && query.trim() && results.length === 0 && (
              <div className="px-2 py-2 text-xs text-muted-foreground">No matching ports</div>
            )}
            {!loading && !error && !query.trim() && (
              <div className="px-2 py-2 text-xs text-muted-foreground">Type a code or port name</div>
            )}
            {!error && results.map((port, index) => (
              <button
                key={port.code}
                type="button"
                onClick={() => selectPort(port)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`flex w-full cursor-pointer items-center justify-between rounded px-2 py-2 text-left text-xs ${
                  highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <span className="min-w-0">
                  <span className="font-semibold text-foreground">{port.code}</span>
                  <span className="ml-2 text-muted-foreground">{port.name}, {port.country_name}</span>
                  {port.alternate_codes && port.alternate_codes.length > 0 && (
                    <span className="ml-2 text-[10px] text-muted-foreground/80">also: {port.alternate_codes.join(', ')}</span>
                  )}
                </span>
                <Badge
                  size="xs"
                  variant="outline"
                  className={port.code_source_label === 'CARRIER' ? 'border-amber-300 bg-amber-100 text-amber-800' : undefined}
                >
                  {port.code_source_label}
                </Badge>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          <Badge
            size="xs"
            variant="outline"
            className={selected.code_source_label === 'CARRIER' ? 'border-amber-300 bg-amber-100 text-amber-800' : undefined}
          >
            {selected.code_source_label}
          </Badge>
          <span>{selected.name}, {selected.country_name}</span>
        </div>
      )}

    </div>
  )
}
